import React, { useState } from 'react';

import { Form } from '../../components/forms/Form';
import { EmailInput } from '../../components/forms/Input';
import { Label } from '../../components/forms/Label';
import Alert from '../../components/alert';
import ButtonDefault from '../../components/forms/Buttons';
import Dropdown from '../../components/Dropdown';
import { type DropdownOption } from '../../components/Dropdown';
import MessageConfigTripGroupDropdown from '../components/messageConfigTripGroupDropdown';
import { BodyCard } from '../components/styledComponent';
import type { MessageConfigurationResponse } from '../../types/messageConfiguration';
import styled from 'styled-components';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Definition = styled.div`
  padding-bottom: 25px;
`;

const InputRowWrapper = styled.div`
  display: flex;
  padding: 5px 5px;
`;

const RowItemWrapper = styled.div`
  display: flex;
  flex: 0 0 ${(props: { width: string }) => props.width};
`;

const SubmitButtonWrapper = styled.div`
  padding-top: 25px;
`;

interface TestMessagingFormProps {
  onSendTestEmailMessage: (
    messageConfigId: string,
    testMessageType: string,
    email: string,
  ) => void,
  sentTestMessage: boolean,
  messageConfigs: MessageConfigurationResponse,
  sendErrorMessage?: string,
  getTripGroupsErrorMessage?: string,
};

export const TestMessagingForm: React.FC<TestMessagingFormProps> = ({
  onSendTestEmailMessage,
  sendErrorMessage,
  getTripGroupsErrorMessage,
  sentTestMessage,
  messageConfigs,
}) => {
  const testMessageOptions: DropdownOption<string>[] = [
    { text: 'Opt In', value: 'EMAIL_OPT_IN' },
    {
      text: 'Trip Reminder',
      value: 'EMAIL_TRIP_REMINDER',
    },
    {
      text: 'Connection Information',
      value: 'EMAIL_CONNECTION_INFO',
    },
    {
      text: 'Cancellation',
      value: 'EMAIL_FLIGHT_CANCELLATION',
    },
    {
      text: 'Gate Change',
      value: 'EMAIL_LEG_DEPARTURE_GATE_CHANGE',
    },
    {
      text: 'Departure Delay',
      value: 'EMAIL_LEG_DEPARTURE_DELAY',
    },
    {
      text: 'Diversion',
      value: 'EMAIL_FLIGHT_DIVERSION',
    },
    {
      text: 'Departure Information',
      value: 'EMAIL_LEG_DEPARTURE_INFO',
    },
    {
      text: 'Waiver',
      value: 'EMAIL_WAIVER_MATCH',
    },
  ];

  const [emailInput, setEmailInput] = useState('');
  const [messageConfigId, updateMessageConfigId] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>();
  const [testMessageTypeInput, setTestMessageTypeInput] = useState<string>(
    String(testMessageOptions[0].value || ''),
  );
  const [sentRecipient, updateSentRecipient] = useState('');

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    updateSentRecipient(emailInput);
    onSendTestEmailMessage(messageConfigId, testMessageTypeInput, emailInput);
  };

  const isErrorOrRequiredFieldEmpty = !!emailErrorMessage || emailInput === '';
  const tripGroupDropdownId = 'tripGroupDropdown';
  return (
    <>
      {sendErrorMessage && <Alert type="danger" message={sendErrorMessage} />}

      {!sendErrorMessage && (
        <BodyCard>
          <FormContainer>
            {sentTestMessage && (
              <div data-fs-exclude className="fs-exclude">
                <Alert
                  type="success"
                  timeoutInMilliSeconds={5000}
                  message={`Successfully sent message to ${sentRecipient}.`}
                />
              </div>
            )}
            <Definition>
              <p>
                This page can be used to send test alert messages for a sample
                set of commonly applied message types. The sent message will be
                formatted based on your account’s Email Configuration settings
                for the specified Trip Group.
              </p>
            </Definition>
            <Form onSubmit={handleFormSubmit}>
              <InputRowWrapper>
                <RowItemWrapper width="160px">
                  <Label htmlFor={tripGroupDropdownId} text="Trip Group:" />
                </RowItemWrapper>
                <RowItemWrapper width="320px">
                  <MessageConfigTripGroupDropdown
                    id={tripGroupDropdownId}
                    onSelect={updateMessageConfigId}
                    messageConfigs={messageConfigs}
                    errorMessage={getTripGroupsErrorMessage}
                  />
                </RowItemWrapper>
              </InputRowWrapper>

              <InputRowWrapper>
                <RowItemWrapper width="160px">
                  <Label htmlFor="emailId" text="Email:" />
                </RowItemWrapper>
                <RowItemWrapper width="320px">
                  <EmailInput
                    name="email"
                    id="emailId"
                    placeholder="john@doe.com"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                      setEmailInput(e.currentTarget.value);
                    }}
                    onError={(emailErrorMessage?: string): void => {
                      setEmailErrorMessage(emailErrorMessage);
                    }}
                    value={emailInput}
                  />
                </RowItemWrapper>
              </InputRowWrapper>

              <InputRowWrapper>
                <RowItemWrapper width="160px">
                  <Label
                    htmlFor="sampleTestMessageTypeDropdown"
                    text="Sample Message Type:"
                  />
                </RowItemWrapper>
                <RowItemWrapper width="320">
                  <Dropdown
                    id="sampleTestMessageTypeDropdown"
                    value={testMessageTypeInput}
                    onChange={value => {
                      value && setTestMessageTypeInput(String(value));
                    }}
                    options={testMessageOptions}
                  />
                </RowItemWrapper>
              </InputRowWrapper>

              <SubmitButtonWrapper>
                <ButtonDefault
                  id="sendTestMessageButton"
                  disabled={isErrorOrRequiredFieldEmpty}
                  type="submit"
                >
                  SEND TEST MESSAGE
                </ButtonDefault>
              </SubmitButtonWrapper>
            </Form>
          </FormContainer>
        </BodyCard>
      )}
    </>
  );
};
